import React, { useRef, useState, useEffect } from "react";
import {
  Typography,
  Paper,
  Button,
  Radio,
  FormControlLabel
} from "@material-ui/core";
import { useTranslation } from "@/langs/useTranslation";
import { useHistory } from "react-router-dom";
import { BreadCrumbs } from "@/components/device/BreadCrumbs";
import useStyles from "@/styles/groupsStyle";
import {
  IconFile,
  IconTemplateFullscreen,
  IconTemplatePldm
} from "@/components/Icon";
import { FormItem } from "@/components/Form";
import SearchInput from "@/components/SearchInput";
import GroupDevicesModal from "@/components/FormFields/DeviceGroups/GroupDevicesModal";
import { useFormik } from "formik";
import DropdownButton from "@/components/DropdownButton";
import {
  applyCMSTemplate,
  changeCMSContentDisplay,
  changeCMSContentStatus,
  getCMSTemplateApplyedDeivces,
  getCMSTemplateType
} from "@/apis/apiCollection";
import SingleSelector from "@/components/SingleSelector";
import DateRangePicker from "@/components/DateRangePicker";
import SubmitButton from "@/components/commons/SubmitButton";
import CMSTemplateDatatable from "../CMSTemplateDatatable";
import * as dayjs from "dayjs";
import modalConfirm from "@/components/Modal/modalConfirm";
import { toast } from "@/apis/request";
import { useGlobalState } from "@/GlobalProvider";
import HasPermission from "@/components/commons/HasPermission";
import { fullWindowTemplateId, pldmTemplateId } from "./CONSTANT";

// import TemplateList from "./TemplateList";

const Index = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const tableRef = useRef(null);

  const [total, setTotal] = useState(0);
  const [devicesModalOpen, setDevicesModalOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [applyDevices, setApplyDevices] = useState([]);
  const [types, setTypes] = useState([
    { id: "", name: t({ defaultMessage: "全部" }) }
  ]);
  const applyTemplateId = useRef("");
  const { currentCompanyId } = useGlobalState();

  const handleCreateTemplate = templateId => {
    switch (templateId) {
      case fullWindowTemplateId:
        history.push("/cms/template/full-screen/create");
        break;
      case pldmTemplateId:
        history.push("/cms/template/pldm/create");
        break;
      default:
    }
  };

  useEffect(() => {
    getCMSTemplateType().then(({ contentTemplateTypeList }) => {
      setTemplates(
        contentTemplateTypeList.map(v => ({
          name: v.name,
          key: v.id,
          onClick: () => handleCreateTemplate(v.id),
          Icon: getIconByTemplateId(v.id),
          iconProps: { style: { marginLeft: "auto", paddingLeft: 30 } }
        }))
      );
      setTypes([
        { id: "", name: t({ defaultMessage: "全部" }) },
        ...contentTemplateTypeList.map(v => ({
          id: v.id,
          name: v.name
        }))
      ]);
    });
  }, [currentCompanyId]);

  const initialValues = {
    keywords: "",
    contentTemplateTypeId: "",
    status: 0,
    createTime: [null, null],
    lastUpdateTime: [null, null],
    effectTime: [null, null]
  };

  const { values, handleChange, handleSubmit, handleReset } = useFormik({
    initialValues,
    onSubmit: () => {
      handleReloadDataTable();
    }
  });

  const handleLoaded = json => {
    setTotal(json.recordsTotal);
  };

  const handleReloadDataTable = () => {
    // reload
    if (tableRef.current) tableRef.current.reload();
  };

  const resetQuery = () => {
    handleReset();
    setTimeout(() => {
      handleReloadDataTable();
    });
  };

  const getSearchParams = () => {
    // const formValues = isReset.current ? initialValues : values;
    return {
      keywords: values.keywords,
      contentTemplateTypeId: values.contentTemplateTypeId,
      status: values.status,
      createBeginTime: values.createTime[0]
        ? dayjs(values.createTime[0]).valueOf()
        : 0,
      createEndTime: values.createTime[1]
        ? dayjs(values.createTime[1]).valueOf()
        : 0,
      lastUpdateBeginTime: values.lastUpdateTime[0]
        ? dayjs(values.lastUpdateTime[0]).valueOf()
        : 0,
      lastUpdateEndTime: values.lastUpdateTime[1]
        ? dayjs(values.lastUpdateTime[1]).valueOf()
        : 0,
      effectBeginTime: values.effectTime[0]
        ? dayjs(values.effectTime[0]).valueOf()
        : 0,
      effectEndTime: values.effectTime[1]
        ? dayjs(values.effectTime[1]).valueOf()
        : 0
    };
  };

  const handlePlay = async data => {
    const arr = Array.isArray(data) ? data : [data];
    await changeCMSContentDisplay({
      contentTemplateIds: arr.map(v => v.id),
      display: 1
    });
    toast.success(t({ defaultMessage: "播放成功" }));
    handleReloadDataTable();
  };

  const handlePause = async data => {
    const arr = Array.isArray(data) ? data : [data];
    await changeCMSContentDisplay({
      contentTemplateIds: arr.map(v => v.id),
      display: 2
    });
    toast.success(t({ defaultMessage: "暂停成功" }));
    handleReloadDataTable();
  };

  const handleApply = async data => {
    const { deviceIds } = await getCMSTemplateApplyedDeivces({
      contentTemplateId: data.id
    });
    setApplyDevices(deviceIds);
    setDevicesModalOpen(true);
    applyTemplateId.current = data.id;
  };

  const handlePublish = async data => {
    const arr = Array.isArray(data) ? data : [data];
    modalConfirm({
      title: t({ defaultMessage: "发布" }),
      content: (
        <FormControlLabel
          value="immediate"
          control={<Radio checked value="immediate" color="primary" />}
          label={t({ defaultMessage: "立即发布" })}
          labelPlacement="end"
        />
      ),
      onConfirm: async () => {
        await changeCMSContentStatus({
          contentTemplateIds: arr.map(v => v.id),
          status: 10
        });
        toast.success(t({ defaultMessage: "发布成功" }));
        handleReloadDataTable();
      }
    });
  };

  const handleDelete = async data => {
    const arr = Array.isArray(data) ? data : [data];
    modalConfirm({
      title: t({ defaultMessage: "删除提醒" }),
      content: t(
        {
          defaultMessage: "是否确认删除模板({name}）"
        },
        { name: arr.map(v => v.name).join(", ") }
      ),
      onConfirm: async () => {
        await changeCMSContentStatus({
          contentTemplateIds: arr.map(v => v.id),
          status: 100
        });
        handleReloadDataTable();
        t({ defaultMessage: "删除成功" });
      }
    });
  };

  const handleBatchDelete = () => {
    const datas = tableRef.current.getSelectedRows();
    if (datas.length == 0) return;
    handleDelete(datas);
  };

  const handleBatchPublish = () => {
    const datas = tableRef.current.getSelectedRows();
    if (datas.length == 0) return;
    handlePublish(datas);
  };

  const handleBatchPlay = () => {
    const datas = tableRef.current.getSelectedRows();
    if (datas.length == 0) return;
    handlePlay(datas);
  };

  const handleBatchPause = () => {
    const datas = tableRef.current.getSelectedRows();
    if (datas.length == 0) return;
    handlePause(datas);
  };

  const handleGroupsConfirm = async (checkIds, checkedNodes) => {
    await applyCMSTemplate({
      contentTemplateId: applyTemplateId.current,
      deviceIds: checkIds
    });
    toast.success(t({ defaultMessage: "应用成功" }));
    setDevicesModalOpen(false);
    setApplyDevices([]);
  };

  useEffect(() => {
    resetQuery();
  }, [currentCompanyId]);

  return (
    <>
      <BreadCrumbs
        noTranslate
        pageName={t({ defaultMessage: "内容模板列表" })}
      />
      <Typography variant="h4" style={{ marginBottom: 24 }}>
        {t({ defaultMessage: "内容模板列表" })}
      </Typography>
      <Paper
        elevation={0}
        style={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          overflow: "auto"
        }}
      >
        <form onSubmit={handleSubmit}>
          <div className={classes.tableHeader}>
            <div style={{ display: "flex" }}>
              <div>
                <Typography variant="h5" component="span">
                  {t({ defaultMessage: "内容模板列表" })}
                </Typography>
                <Typography
                  variant="body2"
                  color="primary"
                  component="span"
                  className={classes.subTitle}
                  style={{ marginLeft: 24 }}
                >
                  <IconFile size={15} style={{ marginRight: 8 }} />
                  {t({ defaultMessage: "{num}条" }, { num: total })}
                </Typography>
              </div>
              <div style={{ display: "flex", marginLeft: "auto" }}>
                <SearchInput
                  placeholder={t({ defaultMessage: "搜索" })}
                  name="keywords"
                  icon={null}
                  value={values.keywords}
                  onChange={handleChange}
                  style={{ marginRight: 24 }}
                />
                <HasPermission permission="content_template_list_create_template">
                  <DropdownButton
                    title={t({ defaultMessage: "创建模板" })}
                    menu={templates}
                    paperAutoWidth
                  />
                </HasPermission>
              </div>
            </div>
            <div className={classes.tableQueryForm}>
              <FormItem
                label={t({ defaultMessage: "类型" })}
                className={classes.formItem}
              >
                <SingleSelector
                  // placeholder={t({ defaultMessage: "选择" })}
                  clearable={false}
                  options={types}
                  name="contentTemplateTypeId"
                  value={values.contentTemplateTypeId}
                  onChange={handleChange}
                />
              </FormItem>
              <FormItem
                label={t({ defaultMessage: "模板状态" })}
                className={classes.formItem}
              >
                <SingleSelector
                  // placeholder={t({ defaultMessage: "选择状态" })}
                  clearable={false}
                  options={[
                    { id: 0, name: t({ defaultMessage: "全部" }) },
                    { id: 1, name: t({ defaultMessage: "草稿" }) },
                    { id: 10, name: t({ defaultMessage: "已发布" }) }
                  ]}
                  name="status"
                  value={values.status}
                  onChange={handleChange}
                />
              </FormItem>
              <FormItem
                label={t({ defaultMessage: "创建时间" })}
                className={classes.formItem}
              >
                <DateRangePicker
                  name="createTime"
                  value={values.createTime}
                  onChange={handleChange}
                />
              </FormItem>
              <FormItem
                label={t({ defaultMessage: "最后修改时间" })}
                className={classes.formItem}
              >
                <DateRangePicker
                  name="lastUpdateTime"
                  value={values.lastUpdateTime}
                  onChange={handleChange}
                />
              </FormItem>
              <FormItem
                label={t({ defaultMessage: "最后发布时间" })}
                className={classes.formItem}
              >
                <DateRangePicker
                  name="effectTime"
                  value={values.effectTime}
                  onChange={handleChange}
                />
              </FormItem>
              <FormItem className={classes.formItem}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  style={{ marginRight: 8 }}
                >
                  {t({ defaultMessage: "搜索" })}
                </Button>
                <Button variant="outlined" onClick={resetQuery}>
                  {t({ defaultMessage: "重置" })}
                </Button>
              </FormItem>
            </div>
          </div>
        </form>
        <CMSTemplateDatatable
          ref={tableRef}
          getSearchParams={getSearchParams}
          onView={(e, data) => {
            if (data.contentTemplateTypeId === fullWindowTemplateId)
              history.push(`/cms/template/full-screen/view/${data.id}`);
            if (data.contentTemplateTypeId === pldmTemplateId)
              history.push(`/cms/template/pldm/view/${data.id}`);
          }}
          onEdit={data => {
            if (data.contentTemplateTypeId === fullWindowTemplateId)
              history.push(`/cms/template/full-screen/edit/${data.id}`);
            if (data.contentTemplateTypeId === pldmTemplateId)
              history.push(`/cms/template/pldm/edit/${data.id}`);
          }}
          onApply={handleApply}
          onPublish={handlePublish}
          onDelete={handleDelete}
          onLoaded={handleLoaded}
          onPlay={handlePlay}
          onPause={handlePause}
          footer={
            <>
              <HasPermission permission="content_template_list_play_template">
                <SubmitButton
                  variant="outlined"
                  color="primary"
                  // loading={loading && operateType.current === "disable"}
                  onClick={handleBatchPlay}
                >
                  {t({ defaultMessage: "批量播放" })}
                </SubmitButton>
              </HasPermission>
              <HasPermission permission="content_template_list_pause_template">
                <SubmitButton
                  variant="outlined"
                  color="primary"
                  // loading={loading && operateType.current === "disable"}
                  onClick={handleBatchPause}
                >
                  {t({ defaultMessage: "批量暂停" })}
                </SubmitButton>
              </HasPermission>
              <HasPermission permission="content_template_list_publish_template">
                <SubmitButton
                  variant="outlined"
                  color="primary"
                  // loading={loading && operateType.current === "enable"}
                  onClick={handleBatchPublish}
                >
                  {t({ defaultMessage: "批量发布" })}
                </SubmitButton>
              </HasPermission>
              <HasPermission permission="content_template_list_delete_template">
                <SubmitButton
                  variant="outlined"
                  color="primary"
                  // loading={loading && operateType.current === "delete"}
                  onClick={handleBatchDelete}
                >
                  {t({ defaultMessage: "批量删除" })}
                </SubmitButton>
              </HasPermission>
            </>
          }
        />
      </Paper>
      <GroupDevicesModal
        open={devicesModalOpen}
        checkedKeys={applyDevices}
        onClose={() => {
          setDevicesModalOpen(false);
          setApplyDevices([]);
        }}
        onConfirm={handleGroupsConfirm}
        dontCheck
      />
    </>
  );
};

const getIconByTemplateId = id => {
  switch (id) {
    case fullWindowTemplateId:
      return IconTemplateFullscreen;
    case pldmTemplateId:
      return IconTemplatePldm;
    default:
      return;
  }
};

export default Index;
